/**
 * After the WebSocket drops mid-run, the agent keeps posting to the old
 * connection, so the `end` event never reaches this tab and the conversation
 * spins for ever. Once the socket is back, poll the conversation from the
 * database until the server says the run has settled, then apply that copy.
 */

export type ResyncStatus =
  | 'thinking'
  | 'running'
  | 'completed'
  | 'failed'
  | 'stopped';

const SETTLED: ReadonlySet<ResyncStatus> = new Set([
  'completed',
  'failed',
  'stopped',
]);

export const isSettledStatus = (status: ResyncStatus | string): boolean =>
  SETTLED.has(status as ResyncStatus);

/**
 * GET /api/core/pleri/conversations/:id answers
 * `{ conversation: { status, … }, messages, contextUsage }`, while the store's
 * DbConversation type expects `status` at the top level. Lift it so status
 * checks see the real value.
 */
export function liftConversationStatus<
  T extends { status?: string; conversation?: { status?: string } },
>(conversation: T): T & { status?: string } {
  if (conversation.status !== undefined) {
    return conversation;
  }
  const nested = conversation.conversation?.status;
  return nested === undefined
    ? conversation
    : { ...conversation, status: nested };
}

export const RESYNC_INTERVAL_MS = 15_000;
// A run is capped at 15 minutes server-side; 20 minutes of polling covers it.
export const RESYNC_MAX_ATTEMPTS = 80;

interface ResyncOptions<T extends { status: ResyncStatus | string }> {
  conversationId: string;
  fetch: (conversationId: string) => Promise<T | null>;
  apply: (conversationId: string, conversation: T) => void;
  /** False once the socket delivered the end event after all. */
  stillWaiting: () => boolean;
  intervalMs?: number;
  maxAttempts?: number;
  /** Called once when polling ends, whatever the reason. */
  onStop?: (conversationId: string) => void;
}

/** Returns a cancel function. */
export function resyncUntilSettled<
  T extends { status: ResyncStatus | string },
>({
  conversationId,
  fetch,
  apply,
  stillWaiting,
  intervalMs = RESYNC_INTERVAL_MS,
  maxAttempts = RESYNC_MAX_ATTEMPTS,
  onStop,
}: ResyncOptions<T>): () => void {
  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const stop = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    onStop?.(conversationId);
  };

  const tick = async () => {
    timer = null;
    if (stopped) {
      return;
    }
    if (!stillWaiting()) {
      stop();
      return;
    }
    attempts++;
    const conversation = await fetch(conversationId);
    if (stopped) {
      return;
    }
    if (!stillWaiting()) {
      stop();
      return;
    }
    if (conversation && isSettledStatus(conversation.status)) {
      apply(conversationId, conversation);
      stop();
      return;
    }
    if (attempts < maxAttempts) {
      timer = setTimeout(tick, intervalMs);
    } else {
      stop();
    }
  };

  timer = setTimeout(tick, intervalMs);

  return stop;
}
